/**
 * Energy Consumption Report
 * Energiförbrukningsrapport för fordon (Uppsala krav 5.4.7.2)
 */

import React from 'react';
import { useTranslation } from '@/store/languageStore';
import OrgTreeSelect from '@/components/common/OrgTreeSelect';
import DateRangeFilter from '@/components/common/DateRangeFilter';
import {
  ChartBarIcon,
  ArrowDownTrayIcon,
  TruckIcon,
  BoltIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { useEnergyConsumption } from '@/hooks/useEnergyConsumption';
import {
  CHART_COLORS,
  EnergyTab,
  getConsumptionColorClass,
} from '@/types/energyConsumption';
import { DataTable, ReportPageLayout, LoadingState, SummaryCard } from '@/components/common';
import type { OrganizationEnergyStats, EnergyStats } from '@/types/energyConsumption';

const EnergyConsumptionReport: React.FC = () => {
  const { t } = useTranslation();
  const {
    loading,
    activeTab,
    setActiveTab,
    selectedOrg,
    setSelectedOrg,
    dateRange,
    updateDateRange,
    summary,
    orgStats,
    vehicleStats,
    trendData,
    handleExport,
  } = useEnergyConsumption();

  return (
    <ReportPageLayout
      title={t('energyConsumption.title') || 'Energiförbrukning'}
      description={t('energyConsumption.description') || 'Analysera fordonsflottans bränsleförbrukning'}
      icon={<BoltIcon className="w-6 h-6 text-primary" />}
      actions={
        <button
          onClick={handleExport}
          className="flex items-center gap-2 px-4 py-2 bg-bgPrimary text-textLight rounded-lg hover:bg-bgSecondary"
        >
          <ArrowDownTrayIcon className="h-5 w-5" />
          {t('common.export') || 'Exportera'}
        </button>
      }
      filters={
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-textSecondary mb-1">
              {t('common.organization') || 'Organisation'}
            </label>
            <OrgTreeSelect
              value={selectedOrg}
              onChange={setSelectedOrg}
              placeholder={t('common.selectOrganization') || 'Välj organisation'}
            />
          </div>
          <DateRangeFilter
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={updateDateRange}
          />
        </div>
      }
    >
      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {(['overview', 'organizations', 'vehicles', 'trend'] as const).map(
          (tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as EnergyTab)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === tab
                  ? 'bg-bgPrimary text-textLight'
                  : 'bg-bgCard text-textSecondary hover:bg-bgElevated'
              }`}
            >
              {tab === 'overview' && (t('common.overview') || 'Översikt')}
              {tab === 'organizations' &&
                (t('common.organizations') || 'Organisationer')}
              {tab === 'vehicles' && (t('common.vehicles') || 'Fordon')}
              {tab === 'trend' && (t('common.trend') || 'Trend')}
            </button>
          )
        )}
      </div>

      {loading ? (
        <LoadingState />
      ) : (
        <>
          {/* Overview Tab */}
          {activeTab === 'overview' && summary && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <SummaryCard
                  icon={<TruckIcon className="h-8 w-8 text-blue-500" />}
                  title={t('energyConsumption.totalVehicles') || 'Fordon'}
                  value={summary.totalVehicles.toString()}
                />
                <SummaryCard
                  icon={<ChartBarIcon className="h-8 w-8 text-green-500" />}
                  title={t('energyConsumption.totalDistance') || 'Total distans'}
                  value={`${summary.totalDistance.toLocaleString()} km`}
                />
                <SummaryCard
                  icon={<BoltIcon className="h-8 w-8 text-yellow-500" />}
                  title={t('energyConsumption.totalFuel') || 'Total bränsle'}
                  value={`${summary.totalFuelUsed.toLocaleString()} L`}
                />
                <SummaryCard
                  icon={<ClockIcon className="h-8 w-8 text-purple-500" />}
                  title={
                    t('energyConsumption.avgConsumption') || 'Snittförbrukning'
                  }
                  value={`${summary.avgFuelConsumption} L/100km`}
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-bgCard rounded-lg p-4 shadow-sm">
                  <h3 className="text-lg font-semibold text-textDefault mb-4">
                    {t('energyConsumption.fuelByOrg') ||
                      'Bränsle per organisation'}
                  </h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={orgStats.slice(0, 10)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="organizationName"
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis />
                      <Tooltip />
                      <Bar
                        dataKey="totalFuelUsed"
                        fill="#FFBB28"
                        name="Bränsle (L)"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-bgCard rounded-lg p-4 shadow-sm">
                  <h3 className="text-lg font-semibold text-textDefault mb-4">
                    {t('energyConsumption.consumptionDist') || 'Fördelning'}
                  </h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={orgStats.slice(0, 6)}
                        dataKey="totalFuelUsed"
                        nameKey="organizationName"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label
                      >
                        {orgStats.slice(0, 6).map((_, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={CHART_COLORS[index % CHART_COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {/* Organizations Tab */}
          {activeTab === 'organizations' && (
            <DataTable<OrganizationEnergyStats>
              data={orgStats}
              rowKey="organizationId"
              pageSize={15}
              columns={[
                { key: 'organizationName', label: t('common.organization') || 'Organisation', sortable: true },
                { key: 'vehicleCount', label: t('common.vehicles') || 'Fordon', align: 'right', sortable: true },
                { key: 'totalTrips', label: t('common.trips') || 'Resor', align: 'right', sortable: true },
                { key: 'totalDistance', label: t('common.distance') || 'Distans', align: 'right', sortable: true, render: (v) => `${(v as number).toLocaleString()} km` },
                { key: 'totalFuelUsed', label: t('energyConsumption.fuel') || 'Bränsle', align: 'right', sortable: true, render: (v) => `${(v as number).toLocaleString()} L` },
                { key: 'avgFuelConsumption', label: t('energyConsumption.consumption') || 'Förbrukning', align: 'right', sortable: true, render: (_, row) => <span className={`px-2 py-1 rounded ${getConsumptionColorClass(row.avgFuelConsumption)}`}>{row.avgFuelConsumption} L/100km</span> },
              ]}
              features={{ csvExport: true, search: true, stickyHeader: true }}
            />
          )}

          {/* Vehicles Tab */}
          {activeTab === 'vehicles' && (
            <DataTable<EnergyStats>
              data={vehicleStats}
              rowKey="vehicleId"
              pageSize={15}
              columns={[
                { key: 'registrationNumber', label: t('common.vehicle') || 'Fordon', sortable: true, render: (v, row) => <div><div className="font-medium">{String(v ?? '')}</div><div className="text-sm text-textSecondary">{row.make} {row.model}</div></div> },
                { key: 'organizationName', label: t('common.organization') || 'Organisation', sortable: true },
                { key: 'totalTrips', label: t('common.trips') || 'Resor', align: 'right', sortable: true },
                { key: 'totalDistance', label: t('common.distance') || 'Distans', align: 'right', sortable: true, render: (v) => `${(v as number).toLocaleString()} km` },
                { key: 'totalFuelUsed', label: t('energyConsumption.fuel') || 'Bränsle', align: 'right', sortable: true, render: (_, row) => row.isElectric ? '-' : `${(row.totalFuelUsed as number).toLocaleString()} L` },
                { key: 'wltpConsumption', label: t('energyConsumption.wltp') || 'WLTP', align: 'right', sortable: true, render: (v, row) => v ? `${String(v)} ${row.isElectric ? 'kWh/100km' : 'L/100km'}` : (row.isElectric ? '⚡ Elbil' : '-') },
                { key: 'avgFuelConsumption', label: t('energyConsumption.consumption') || 'Förbrukning', align: 'right', sortable: true, render: (_, row) => row.isElectric ? '-' : <span className={`px-2 py-1 rounded ${getConsumptionColorClass(row.avgFuelConsumption, row.wltpConsumption)}`}>{row.avgFuelConsumption} L/100km</span> },
                { key: 'avgSpeed', label: t('energyConsumption.avgSpeed') || 'Snitthast.', align: 'right', sortable: true, render: (v) => `${Number(v ?? 0)} km/h` },
              ]}
              features={{ csvExport: true, search: true, stickyHeader: true }}
            />
          )}

          {/* Trend Tab */}
          {activeTab === 'trend' && (
            <div className="space-y-6">
              <div className="bg-bgCard rounded-lg p-4 shadow-sm">
                <h3 className="text-lg font-semibold text-textDefault mb-4">
                  {t('energyConsumption.consumptionTrend') ||
                    'Förbrukningstrend'}
                </h3>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip />
                    <Legend />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="totalFuelUsed"
                      stroke="#FFBB28"
                      name="Bränsle (L)"
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="avgFuelConsumption"
                      stroke="#8884D8"
                      name="Förbrukning (L/100km)"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-bgCard rounded-lg p-4 shadow-sm">
                <h3 className="text-lg font-semibold text-textDefault mb-4">
                  {t('energyConsumption.distanceTrend') || 'Distanstrend'}
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="totalDistance"
                      fill="#00C49F"
                      name="Distans (km)"
                    />
                    <Bar dataKey="tripCount" fill="#0088FE" name="Antal resor" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </>
      )}
    </ReportPageLayout>
  );
};

export default EnergyConsumptionReport;
